import { useRef, useEffect } from "react";
import classNames from "classnames";
import ListItem from "@/components/ListItem";
import { mentionListItemProps } from "./mentionListItem";
import type { MentionOverlayCopy, MentionUser } from "./types";

function StatusMessage({ children, testId, role }: { children: React.ReactNode; testId?: string; role?: "alert" }) {
  return (
    <div
      className="py-3 px-4 text-center text-body-medium text-sys-brand-on-surface-variant"
      data-testid={testId}
      role={role}
      aria-live={role === "alert" ? "assertive" : undefined}
    >
      {children}
    </div>
  );
}

const DEFAULT_OVERLAY: Required<MentionOverlayCopy> = {
  prompt: "Mention a teammate",
  empty: "No teammates found",
  error: "",
  ariaLabel: "Mention a teammate"
};

interface AtMentionOverlayProps {
  users: MentionUser[];
  isLoading?: boolean;
  query: string;
  selectedIndex: number;
  onSelect: (user: MentionUser) => void;
  onHighlightChange: (index: number) => void;
  className?: string;
  overlay?: MentionOverlayCopy;
}

export default function AtMentionOverlay({
  users,
  isLoading = false,
  query,
  selectedIndex,
  onSelect,
  onHighlightChange,
  className,
  overlay
}: AtMentionOverlayProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  itemRefs.current = itemRefs.current.slice(0, users.length);
  const copy = { ...DEFAULT_OVERLAY, ...overlay };

  useEffect(() => {
    if (selectedIndex < 0 || !listRef.current) return;
    const item = itemRefs.current[selectedIndex];
    if (!item) return;

    const parent = listRef.current;
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const parentTop = parent.scrollTop;
    const parentBottom = parentTop + parent.clientHeight;

    if (itemTop < parentTop) {
      parent.scrollTo({ top: itemTop });
    } else if (itemBottom > parentBottom) {
      parent.scrollTo({ top: itemBottom - parent.clientHeight });
    }
  }, [selectedIndex]);

  const hasQuery = query.trim().length > 0;
  const showRows = users.length > 0 && (hasQuery || users.some((user) => user.kind === "back"));

  return (
    <div
      className={classNames("bg-sys-brand-surface shadow-md rounded-md", className ?? "min-w-[420px]")}
      data-testid="at-mention-overlay"
    >
      {copy.error ? (
        <StatusMessage testId="at-mention-overlay-error" role="alert">
          {copy.error}
        </StatusMessage>
      ) : null}
      <div ref={listRef} role="listbox" aria-label={copy.ariaLabel} className="max-h-80 overflow-y-auto">
        {!hasQuery && !isLoading && <StatusMessage testId="at-mention-overlay-prompt">{copy.prompt}</StatusMessage>}

        {isLoading && users.length === 0 && (
          <StatusMessage testId="at-mention-overlay-loading">Searching...</StatusMessage>
        )}

        {hasQuery && !isLoading && users.length === 0 && (
          <StatusMessage testId="at-mention-overlay-empty">{copy.empty}</StatusMessage>
        )}

        {showRows &&
          users.map((user, index) => (
            <div
              key={user.id}
              id={`at-mention-option-${user.id}`}
              role="option"
              aria-selected={index === selectedIndex}
              className={classNames(
                "px-3 py-2 cursor-pointer transition-colors hover:bg-sys-brand-surface-container-high",
                index === selectedIndex && "bg-sys-brand-surface-container-high"
              )}
              onClick={() => onSelect(user)}
              onMouseEnter={() => onHighlightChange(index)}
              ref={(el) => (itemRefs.current[index] = el)}
              data-testid={`at-mention-option-${user.id}`}
            >
              <ListItem {...mentionListItemProps(user, query)} />
            </div>
          ))}
      </div>
    </div>
  );
}
